import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Slide {
  image: string;
  pretitle: string;
  title: string;
  subtitle: string;
  ctaPrimary: { label: string; to: string };
  ctaSecondary: { label: string; to: string };
}

export default function HeroCarousel() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const slides: Slide[] = [
    {
      image: 'https://readdy.ai/api/search-image?query=Modern%20industrial%20engineering%20facility%20at%20night%20with%20steel%20blue%20lighting%2C%20precision%20machinery%20and%20diverse%20engineering%20team%20working%2C%20dramatic%20cinematic%20atmosphere%20with%20copper%20warm%20highlights%2C%20professional%20corporate%20photography&width=1600&height=900&seq=hero-slide-01&orientation=landscape',
      pretitle: t('home.hero.subtitle'),
      title: 'DEIASPART',
      subtitle: t('home.hero.tagline'),
      ctaPrimary: { label: t('home.hero.ctaServices'), to: '/services' },
      ctaSecondary: { label: t('home.hero.ctaContact'), to: '/contact' },
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Diverse%20team%20of%20African%20engineers%20and%20technicians%20in%20hard%20hats%20collaborating%20on%20construction%20site%2C%20steel%20blue%20and%20warm%20copper%20color%20palette%2C%20modern%20infrastructure%20project%20in%20Angola%2C%20professional%20documentary%20photography%20with%20dramatic%20lighting&width=1600&height=900&seq=hero-slide-02&orientation=landscape',
      pretitle: t('home.about.title'),
      title: t('home.hero.slide2.title'),
      subtitle: t('home.about.text1').substring(0, 140) + '...',
      ctaPrimary: { label: t('home.about.cta'), to: '/about' },
      ctaSecondary: { label: t('nav.contact'), to: '/contact' },
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Advanced%20electromechanical%20and%20HVAC%20systems%20installation%20in%20modern%20building%2C%20diverse%20technical%20team%20including%20black%20African%20white%20European%20and%20mixed%20race%20professionals%20working%20together%2C%20steel%20blue%20machinery%20with%20warm%20copper%20ambient%20lighting%2C%20professional%20industrial%20photography&width=1600&height=900&seq=hero-slide-03&orientation=landscape',
      pretitle: t('home.services.title'),
      title: t('home.hero.slide3.title'),
      subtitle: t('home.services.subtitle'),
      ctaPrimary: { label: t('home.services.viewAll'), to: '/services' },
      ctaSecondary: { label: t('home.cta.button'), to: '/contact' },
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20modern%20Luanda%20Angola%20skyline%20at%20golden%20hour%20with%20steel%20blue%20sky%20and%20copper%20sunset%20reflections%2C%20contemporary%20architecture%2C%20inspiring%20urban%20landscape%20photography%20with%20vibrant%20warm%20tones&width=1600&height=900&seq=hero-slide-04&orientation=landscape',
      pretitle: 'Luanda, Angola',
      title: t('nav.contact'),
      subtitle: t('contact.hero.subtitle'),
      ctaPrimary: { label: t('nav.contact'), to: '/contact' },
      ctaSecondary: { label: t('nav.services'), to: '/services' },
    },
  ];

  const goTo = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 700);
  }, [isAnimating]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, slides.length, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, slides.length, goTo]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrent((prevIdx) => (prevIdx + 1) % slides.length);
    }, 6000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [slides.length]);

  return (
    <section className="relative h-[520px] md:h-[700px] flex items-center justify-center overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
        <div className="bg-black/30 backdrop-blur-md rounded-2xl border border-background-50/10 p-6 md:p-10 lg:p-12">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`transition-all duration-700 ${
                index === current
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-6 absolute inset-0 pointer-events-none'
              }`}
            >
              {index === current && (
                <>
                  <p className="text-accent-400 font-medium text-sm md:text-base tracking-[0.2em] uppercase mb-3">
                    {slide.pretitle}
                  </p>
                  <h1 className="font-heading font-bold text-4xl md:text-6xl lg:text-7xl text-background-50 mb-4 tracking-tight">
                    {slide.title}
                  </h1>
                  <p className="text-background-200 text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
                    {slide.subtitle}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Link
                      to={slide.ctaPrimary.to}
                      className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                    >
                      {slide.ctaPrimary.label}
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                    <Link
                      to={slide.ctaSecondary.to}
                      className="inline-flex items-center gap-2 bg-background-50/10 hover:bg-background-50/20 text-background-50 border border-background-50/30 px-7 py-3.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                    >
                      {slide.ctaSecondary.label}
                    </Link>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/10 hover:bg-background-50/20 border border-background-50/20 flex items-center justify-center text-background-50 transition-colors"
        aria-label={t('hero.carousel.prev')}
      >
        <i className="ri-arrow-left-s-line text-xl w-6 h-6 flex items-center justify-center"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/10 hover:bg-background-50/20 border border-background-50/20 flex items-center justify-center text-background-50 transition-colors"
        aria-label={t('hero.carousel.next')}
      >
        <i className="ri-arrow-right-s-line text-xl w-6 h-6 flex items-center justify-center"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 md:gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`transition-all duration-300 rounded-full ${
              index === current
                ? 'w-8 md:w-10 h-2.5 bg-accent-400'
                : 'w-2.5 h-2.5 bg-background-50/40 hover:bg-background-50/60'
            }`}
            aria-label={`${t('hero.carousel.goto')} ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}